import {
    Collections,
    Infraction,
    PenaltyDescriptionByType,
    PenaltyType,
    useCollection,
    useCurrentSeason,
    useSeasonOptions,
} from "api";
import MaterialTable from "material-table";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import {
    AddButton,
    BoxCard,
    ConfirmationDialog,
    Message,
    PageLayout,
} from "ui/componentes";
import { LocalizationPTBr } from "util/MaterialTableUtil";
import { useHistory } from "react-router";
import CustomSelect from "ui/componentes/Form/SelectFieldFormik/CustomSelect";
import { Add as AddIcon } from "@material-ui/icons";
import { Box, Grid } from "@material-ui/core";
import { CopyInfractionsFromSeason } from "ui/views/InfractionViews/CopyInfractionsFromSeason";

const InfractionList = () => {
    const history = useHistory();
    const currentSeason = useCurrentSeason();
    const [selectedSeason, setSelectedSeason] = useState("");

    useEffect(() => {
        if (!selectedSeason) {
            setSelectedSeason(currentSeason?.id);
        }
    }, [currentSeason, selectedSeason]);

    const seasonsOptions = useSeasonOptions();
    const seasonById = seasonsOptions.reduce(
        (previousValue, currentValue) => ({
            ...previousValue,
            [currentValue.value]: currentValue.label,
        }),
        {}
    );

    const infractions = useCollection<Infraction[]>(
        {
            collection: Collections.INFRACTIONS,
            where: ["seasonId", "==", selectedSeason || "-1"],
            orderBy: ["key", "asc"],
            storeAs: `infractions_${selectedSeason || "-1"}`,
        },
        true
    );
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [
        infractionToDelete,
        setInfractionToDelete,
    ] = useState<Infraction | null>();
    const firestore = useFirestore();
    const { enqueueSnackbar } = useSnackbar();

    return (
        <PageLayout
            title="Infrações"
            boxCardProps={{
                display: "flex",
                flexDirection: "column",
            }}
            showInBoxCard={false}
        >
            <MaterialTable
                components={{
                    Container: BoxCard,
                    Toolbar: () => {
                        return (
                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Message
                                        variant="error"
                                        show={!selectedSeason}
                                        message="Selecione uma temporada para carregar as infrações!"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <CustomSelect
                                            label="Temporada"
                                            fullWidth={false}
                                            options={seasonsOptions}
                                            onChange={(v) =>
                                                setSelectedSeason(
                                                    v.target.value
                                                )
                                            }
                                            value={selectedSeason}
                                            style={{ marginRight: 5 }}
                                        />
                                        <CopyInfractionsFromSeason />
                                    </Box>
                                </Grid>
                            </Grid>
                        );
                    },
                }}
                data={infractions}
                columns={[
                    {
                        field: "key",
                        title: "Item",
                    },
                    {
                        field: "description",
                        title: "Descrição",
                    },
                    {
                        field: "penaltyType",
                        title: "Penalidade",
                        render: (infraction) => {
                            const description =
                                PenaltyDescriptionByType[
                                    infraction.penaltyType || ""
                                ];
                            if (
                                infraction.penaltyType ===
                                PenaltyType.STOP_AND_GO
                            ) {
                                return `${description} de ${infraction.stopAndGoTime} segundos`;
                            }
                            return description;
                        },
                    },
                    {
                        field: "penaltyPoints",
                        title: "Pontos por punição",
                        type: "numeric",
                    },
                    {
                        field: "penaltyPointsAppliedAfter",
                        title: "Aplicar pontos por punição após",
                        type: "numeric",
                        render: (infraction) => {
                            return `${infraction.penaltyPointsAppliedAfter} ${
                                infraction.penaltyPointsAppliedAfter > 1
                                    ? "vezes"
                                    : "vez"
                            }`;
                        },
                    },
                    {
                        field: "penaltyChampPoints",
                        title: "Desconto de pontos no campeonato",
                        type: "numeric",
                    },
                    {
                        field: "seasonId",
                        title: "Temporada",
                        render: (infraction) => {
                            return seasonById[infraction.seasonId] || "";
                        },
                    },
                ]}
                actions={[
                    {
                        icon: "delete",
                        onClick: (event, infraction) => {
                            setInfractionToDelete(infraction as Infraction);
                            setShowConfirmation(true);
                        },
                    },
                    {
                        icon: "edit",
                        onClick: (event, infraction) =>
                            history.push(`/infraction/${infraction["id"]}`),
                    },
                ]}
                localization={{ ...LocalizationPTBr }}
                options={{
                    filtering: true,
                    pageSize: 10,
                    sorting: false,
                    search: false,
                    draggable: false,
                }}
            />
            <AddButton onClick={() => history.push("/infraction")}>
                <AddIcon />
            </AddButton>
            <ConfirmationDialog
                showDialog={showConfirmation}
                message={`Deseja excluir o item ${infractionToDelete?.key}?`}
                onCancel={() => setShowConfirmation(false)}
                onOk={() => {
                    setShowConfirmation(false);

                    firestore
                        .collection(Collections.INFRACTIONS)
                        .doc(infractionToDelete?.id)
                        .delete()
                        .then(() => {
                            enqueueSnackbar(
                                `Infração ${infractionToDelete?.key} excluída!`,
                                { variant: "success" }
                            );
                            setInfractionToDelete(null);
                        })
                        .catch((error) => {
                            console.error(error);
                            enqueueSnackbar(
                                `Erro ao excluir a infração ${infractionToDelete?.key}!`,
                                { variant: "error" }
                            );
                            setInfractionToDelete(null);
                        });
                }}
            />
        </PageLayout>
    );
};

export { InfractionList };
